import React, { useMemo } from 'react';
import cx from 'classnames';
import {
  formatToIconName,
  IconName,
  IconTreeNodeData,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { MentionItem } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionsPlugin';
import { hydrateRichTextVariables, renderRichTextToPlainString } from '../../richtext/ast';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';

type IconProps = IconTreeNodeData & {
  id: string;
  editorClassName?: string;
  mentionItems?: MentionItem[];
  overrideVariables?: Partial<Record<string, string>>;
  isEditor?: boolean;
};

export const Icon = React.forwardRef<HTMLDivElement, IconProps>(
  (
    {
      id,
      className,
      editorClassName,
      name,
      href,
      openInNewTab = true,
      overrideVariables,
      mentionItems: _mentionItems,
      isEditor = false,
      ...props
    },
    ref,
  ) => {
    const [IconComponent, setIconComponent] = React.useState<React.ComponentType<any> | null>(null);
    const iconModuleName = formatToIconName(name);

    React.useEffect(() => {
      const loadIcon = async () => {
        try {
          const iconModule = await import('@heroicons/react/24/outline');
          const Component = iconModule[iconModuleName as IconName];
          setIconComponent(() => Component);
        } catch (error) {
          console.warn(`Failed to load icon: ${iconModuleName}`, error);
        }
      };

      loadIcon();
    }, [iconModuleName]);

    // Transform for container queries, then extract stroke-* classes for the SVG
    const transformedClassName = transformToContainerQueries(className, { isEditor });
    const classes = transformedClassName?.split(/\s+/) || [];
    const svgClasses = classes.filter((cls) => cls.startsWith('stroke-'));
    const containerClasses = classes.filter((cls) => !cls.startsWith('stroke-')).join(' ');

    const resolvedHref = useMemo(() => {
      if (!href) return href;
      const hydrated = typeof href !== 'string' ? hydrateRichTextVariables(href, overrideVariables || {}) : href;
      return typeof hydrated !== 'string' ? renderRichTextToPlainString(hydrated) : hydrated;
    }, [href, overrideVariables]);

    if (!IconComponent) {
      return <div ref={ref} className={cx(containerClasses, editorClassName)} {...props} />;
    }

    const iconElement = (
      <div id={id} ref={ref} className={cx(containerClasses, editorClassName)} {...props}>
        <IconComponent className={cx(svgClasses)} />
      </div>
    );

    if (resolvedHref) {
      return (
        <a href={resolvedHref} target={openInNewTab ? '_blank' : '_self'} rel="noreferrer">
          {iconElement}
        </a>
      );
    }

    return iconElement;
  },
);

Icon.displayName = 'Icon';
